import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Version.css";
import IntegrationReport from './IntegrationReport';

function Version() {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const releases = [
    {
      version: "3.0.0",
      date: "August 5, 2026",
      title: "Bharat Connect v3.0 • Powered by Sampark AI Engine",
      changes: [
        "Introduced BodhAI-style dual-state navigation header: attached announcement bar top state and smooth floating pill header on scroll.",
        "Engineered the full WhatsApp Web Split View layout with active presence indicators and live chat memory.",
        "Added interactive chevron dropdown filter menu for quick filtering (All Chats, Unread, Favourites, Pinned Chats).",
        "Full Light Mode and Dark Mode theme synchronization with persistent local storage across all portal views.",
        "Upgraded empty chat states with pinned v3.0 header and one-click quick action suggestions.",
        "Deployed E2E encryption badges and sub-100ms message delivery verification on Indian network nodes."
      ]
    },
    {
      version: "2.5.0",
      date: "July 20, 2026",
      title: "Sampark AI Engine v2.0 & PWA App Installation",
      changes: [
        "Upgraded Sampark AI Engine v2.0 featuring a 10-message thread memory buffer and Gemini REST failover fallback.",
        "Added native PWA installation support for zero-latency desktop and mobile app experience.",
        "Integrated Indian-aware regional language translator supporting 12+ Indian languages.",
        "Deployed Prism syntax highlighting for code snippets generated inside AI chat threads.",
        "Enhanced real-time telemetry dashboards for monitoring live active sessions and AI inference latency."
      ]
    },
    {
      version: "2.2.0",
      date: "July 5, 2026",
      title: "Security & Identity Assurance",
      changes: [
        "Enhanced account authentication security and multi-layer verification.",
        "Added biometrics and document-based identity assurance verification badge.",
        "Improved custom filter pills with sticky navigation headers.",
        "Optimized audio note recording and instant voice playback speed controls."
      ]
    },
    {
      version: "2.1.0",
      date: "June 16, 2026",
      title: "Easier Settings & New Help Pages",
      changes: [
        "Made the Settings menu much easier to use, similar to WhatsApp.",
        "Your privacy choices and theme (Dark/Light mode) now save instantly.",
        "Added clear, easy-to-read pages for the Help Centre, Terms, Privacy Policy, and FAQs.",
        "Fixed a visual bug where text was overlapping in the Contact Support form.",
        "Updated profile pictures to show a clean icon when a user doesn't upload a photo."
      ]
    },
    {
      version: "2.0.0",
      date: "June 15, 2026",
      title: "Faster AI & Better Security",
      changes: [
        "Upgraded Sampark AI to be much faster and smarter.",
        "Improved the AI so it gives more accurate answers about Bharat Connect.",
        "Fixed some scrolling issues to make the AI chat look cleaner.",
        "Fixed login glitches so you can access your account without any annoying loops.",
        "Made the email verification process smoother and faster.",
        "Ensured that Dark Mode works perfectly on every single page of the app."
      ]
    },
    {
      version: "1.5.0",
      date: "March 24, 2026",
      title: "Smart AI Assistant & Live Settings",
      changes: [
        "Added 'Sampark AI' as your personal smart assistant for Bharat Connect.",
        "You can now tag @Sampark AI in chats, and it will remember your previous messages.",
        "Created a dedicated screen just for chatting with Sampark AI.",
        "Updated the Settings page so your changes save instantly across all devices.",
        "Fixed several background bugs to make the app more reliable.",
        "Synchronized the app design globally for a better experience."
      ]
    },
    {
      version: "1.3.0",
      date: "March 24, 2026",
      title: "New Help Guides & User Reviews",
      changes: [
        "Added a new Help section to answer your questions easily.",
        "Added a scrolling wall of reviews so you can see what others think of the app.",
        "Added more Frequently Asked Questions to help you get started.",
        "Added helpful links to the bottom of the website.",
        "Fixed some visual issues on mobile phones so the app looks great on any screen."
      ]
    },
    {
      version: "1.2.0",
      date: "March 24, 2026",
      title: "Design Updates",
      changes: [
        "Updated the colors and buttons to look more modern and welcoming.",
        "Made sure the version number is correct everywhere in the app.",
        "Added beautiful color gradients to the main buttons.",
        "Improved the spacing so the website looks cleaner."
      ]
    },
    {
      version: "1.1.0",
      date: "March 24, 2026",
      title: "A Beautiful New Homepage",
      changes: [
        "Completely redesigned the homepage to look like a premium, modern app.",
        "Made the text easier to read and added nice glowing effects.",
        "Added new sections explaining how the app works and showing our growth.",
        "Added smooth animations when you scroll down the page."
      ]
    },
    {
      version: "1.0.0",
      date: "March 18, 2026",
      title: "The WhatsApp Desktop Experience",
      changes: [
        "Redesigned the chat screen to look exactly like WhatsApp Web in Dark Mode.",
        "Added a side menu with familiar icons and notification badges.",
        "Made the chat filters (All, Unread) perfectly aligned.",
        "Improved Dark Mode so it's easier on your eyes.",
        "Added an interactive phone mockup to the homepage."
      ]
    },
    {
      version: "0.9.0",
      date: "March 15, 2026",
      title: "The Beginning of Bharat Connect",
      changes: [
        "Launched the first version of the app with instant messaging.",
        "Added secure login using your email and password.",
        "Built the basic chat screen where you can see who is online.",
        "Added a background dashboard to keep the app running smoothly."
      ]
    }
  ];

  return (
    <div className="version-page">
      <nav className="version-nav">
        <div className="version-nav-container">
          <Link to="/" className="version-logo">
            <span style={{ fontSize: "1.3rem" }}>🇮🇳</span>
            <span>Bharat Connect</span>
          </Link>

          <Link to="/" className="version-back-btn">
            <FaArrowLeft /> Back to Home
          </Link>
        </div>
      </nav>

      <div className="version-container">
        <h1 data-aos="fade-down">Release History</h1>
        <p className="version-subtitle" data-aos="fade-down" data-aos-delay="100">
          Track our journey towards the ultimate messaging platform.
        </p>

        <div className="timeline">
          {releases.map((release, index) => (
            <div className="timeline-item" key={release.version} data-aos="fade-up" data-aos-delay={index * 150}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <div className="release-header">
                  <span className="version-tag">v{release.version}</span>
                  <span className="release-date">{release.date}</span>
                </div>
                <h3>{release.title}</h3>
                <ul>
                  {release.changes.map((change, i) => (
                    <li key={i}>{change}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
      <IntegrationReport />
    </div>
  );
}

export default Version;
